import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Building2,
  Users,
  HeartPulse,
  LogOut,
  Lock,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth";
import { PRIVACY_POLICY_URL } from "@/lib/legal";
import { SalonSwitcher } from "@/components/salon-switcher";

const navItems = [
  { title: "Dashboard", icon: LayoutDashboard, href: "/admin" },
  { title: "Salony", icon: Building2, href: "/admin/salons" },
] as const;

const superAdminItems = [
  { title: "Statystyki salonów", icon: HeartPulse, href: "/admin/health" },
  { title: "Organizacje", icon: Users, href: "/admin/orgs" },
] as const;

export function AdminSidebar({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const { logout } = useAuth();
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const allItems = isSuperAdmin
    ? [...navItems, ...superAdminItems]
    : [...navItems];

  return (
    <Sidebar>
      <SidebarHeader className="border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground text-sm font-bold">
            A
          </div>
          <div>
            <p className="text-sm font-semibold">Admin Panel</p>
            <p className="text-xs text-muted-foreground">
              {isSuperAdmin ? "Super Admin" : "Admin"}
            </p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Menu</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {allItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={
                      item.href === "/admin"
                        ? currentPath === "/admin"
                        : currentPath.startsWith(item.href)
                    }
                  >
                    <Link to={item.href}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Aktywny salon</SidebarGroupLabel>
          <SidebarGroupContent className="px-1">
            {/* Click opens the searchable list; picking a salon switches and
                opens its panel. */}
            <SalonSwitcher
              subtitle="Kliknij, aby przełączyć"
              isAdmin
              isOperator={false}
              showAdminLink={false}
              salonPanelLink
            />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Lock />
                <span>Polityka prywatności</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => {
                logout();
                window.location.href = "/login";
              }}
            >
              <LogOut />
              <span>Wyloguj się</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
